import { useEffect, useState } from "react";
import { Loader2, Save, Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";

export default function AdminSettings() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({ phone: "", email: "", location: "", hours: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({ ...form, ...r.data.settings })).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await refresh();
      toast.success(es ? "Configuración guardada" : "Settings saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  const fields = [
    { k: "phone", icon: Phone, es: "Teléfono", en: "Phone", hint: es ? "Se usa también para el botón de WhatsApp" : "Also used for the WhatsApp button" },
    { k: "email", icon: Mail, es: "Correo electrónico", en: "Email" },
    { k: "location", icon: MapPin, es: "Ubicación", en: "Location" },
    { k: "hours", icon: Clock, es: "Horario", en: "Business hours" },
  ];

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light">{es ? "Configuración del negocio" : "Business settings"}</h1>
        <p className="text-muted-foreground text-sm mt-1">{es ? "Estos datos se muestran en la web pública (Contacto y WhatsApp)." : "These details appear on the public site (Contact and WhatsApp)."}</p>
      </div>

      <form onSubmit={save} className="space-y-6 border border-border bg-card p-6 lg:p-8">
        {fields.map((f) => (
          <div key={f.k}>
            <Label className="overline text-muted-foreground flex items-center gap-2"><f.icon className="h-3.5 w-3.5 text-gold" /> {es ? f.es : f.en}</Label>
            <Input value={form[f.k] || ""} onChange={set(f.k)} className="rounded-none mt-1.5" data-testid={`settings-${f.k}`} />
            {f.hint && <p className="text-xs text-muted-foreground mt-1.5">{f.hint}</p>}
          </div>
        ))}
        <Button type="submit" className="rounded-none h-12 px-8" disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {es ? "Guardar cambios" : "Save changes"}</>}
        </Button>
      </form>
    </div>
  );
}
